import React, { useState, useEffect } from 'react';
import { useTenant } from '../contexts/TenantContext';
import { usePermissions } from '../hooks/usePermissions';
import { updateUpworkJobFilters, getJobFilterSuggestions, getProfileFilterSuggestions } from '../services/tenantService';
import { Save, CheckCircle, XCircle, Loader, ChevronDown, ChevronUp, Info, Sparkles } from 'lucide-react';
import { AutocompleteTagInput } from './AutocompleteTagInput';
import { AdminPageLayout } from './AdminPageLayout';
import { AdminCard } from './AdminCard';

const INITIAL_FILTERS = {
  searchTerms: [],
  skills: [],
  category: '',
  jobType: 'ANY',
  experienceLevel: 'ANY',
  verifiedPaymentOnly: false,
  previousClients: false,
  budgetRange: { rangeStart: null, rangeEnd: null },
  hourlyRate: { rangeStart: null, rangeEnd: null },
  clientHiresMin: null,
  clientFeedbackMin: null,
  locations: [],
  dateRange: 'ANY',
  proposalRange: { rangeStart: null, rangeEnd: null },
};

function validateFilters(filters) {
  const errors = {};
  const { budgetRange, hourlyRate, proposalRange } = filters;

  if (budgetRange.rangeStart != null && budgetRange.rangeEnd != null && budgetRange.rangeStart > budgetRange.rangeEnd) {
    errors.budgetRange = 'Min budget cannot exceed max';
  }
  if (budgetRange.rangeStart != null && budgetRange.rangeStart < 0) {
    errors.budgetRange = 'Budget cannot be negative';
  }
  if (hourlyRate.rangeStart != null && hourlyRate.rangeEnd != null && hourlyRate.rangeStart > hourlyRate.rangeEnd) {
    errors.hourlyRate = 'Min rate cannot exceed max';
  }
  if (hourlyRate.rangeStart != null && hourlyRate.rangeStart < 0) {
    errors.hourlyRate = 'Rate cannot be negative';
  }
  if (proposalRange.rangeStart != null && proposalRange.rangeEnd != null && proposalRange.rangeStart > proposalRange.rangeEnd) {
    errors.proposalRange = 'Min proposals cannot exceed max';
  }
  if (proposalRange.rangeStart != null && proposalRange.rangeStart < 0) {
    errors.proposalRange = 'Proposals cannot be negative';
  }

  return errors;
}

function Section({ title, children, defaultOpen = true, className = '' }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`rounded-xl border border-gray-200 bg-white ${className}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="admin-section-toggle flex items-center justify-between w-full px-4 py-3 text-sm font-semibold text-gray-800 hover:text-gray-900"
      >
        {title}
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function RangeInput({ label, value, onChange, error, prefix = '', suffix = '', className = '' }) {
  return (
    <div className={className}>
      <label className="admin-filter-label">{label}</label>
      <div className="grid grid-cols-2 gap-2.5">
        <div className="relative">
          {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
          <input
            type="number"
            value={value.rangeStart ?? ''}
            onChange={(e) => onChange({ ...value, rangeStart: e.target.value ? Number(e.target.value) : null })}
            className={`admin-filter-control ${prefix ? 'pl-6' : ''} ${error ? 'border-red-300' : 'border-gray-300'}`}
            placeholder="Min"
            min="0"
          />
        </div>
        <div className="relative">
          {prefix && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
          <input
            type="number"
            value={value.rangeEnd ?? ''}
            onChange={(e) => onChange({ ...value, rangeEnd: e.target.value ? Number(e.target.value) : null })}
            className={`admin-filter-control ${prefix ? 'pl-6' : ''} ${error ? 'border-red-300' : 'border-gray-300'}`}
            placeholder="Max"
            min="0"
          />
          {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{suffix}</span>}
        </div>
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export function TenantJobFilters() {
  const { tenant, tenantConfig, refreshConfig } = useTenant();
  const { canReadJobFilters, canUpdateJobFilters } = usePermissions();
  const canEdit = canUpdateJobFilters();
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [errors, setErrors] = useState({});
  const [suggestions, setSuggestions] = useState({ skills: [], locations: [], categories: [], source: null });
  const [profileSuggestion, setProfileSuggestion] = useState(null);
  const [profileMeta, setProfileMeta] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);

  // Load existing filters from tenant config
  useEffect(() => {
    if (tenantConfig?.jobFilters?.criteria) {
      const c = tenantConfig.jobFilters.criteria;
      setFilters({
        searchTerms: c.searchTerms || [],
        skills: c.skills || [],
        category: c.category || '',
        jobType: c.jobType || 'ANY',
        experienceLevel: c.experienceLevel || 'ANY',
        verifiedPaymentOnly: c.verifiedPaymentOnly || false,
        previousClients: c.previousClients || false,
        budgetRange: c.budgetRange || { rangeStart: null, rangeEnd: null },
        hourlyRate: c.hourlyRate || { rangeStart: null, rangeEnd: null },
        clientHiresMin: c.clientHiresMin ?? null,
        clientFeedbackMin: c.clientFeedbackMin ?? null,
        locations: c.locations || [],
        dateRange: c.dateRange || 'ANY',
        proposalRange: c.proposalRange || { rangeStart: null, rangeEnd: null },
      });
    }
  }, [tenantConfig]);

  // Load suggestions from DB and profile
  useEffect(() => {
    getJobFilterSuggestions()
      .then(setSuggestions)
      .catch(() => {});

    setProfileLoading(true);
    getProfileFilterSuggestions()
      .then((res) => {
        const data = res?.data || res;
        if (data?.suggested) {
          setProfileSuggestion(data.suggested);
          setProfileMeta(data.profileMeta || null);
        }
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, []);

  const applyProfileSuggestions = () => {
    if (!profileSuggestion) return;
    setFilters((prev) => ({
      ...prev,
      searchTerms: profileSuggestion.searchTerms?.length ? profileSuggestion.searchTerms : prev.searchTerms,
      skills: profileSuggestion.skills?.length ? profileSuggestion.skills : prev.skills,
      jobType: profileSuggestion.jobType || prev.jobType,
      experienceLevel: profileSuggestion.experienceLevel || prev.experienceLevel,
      verifiedPaymentOnly: profileSuggestion.verifiedPaymentOnly ?? prev.verifiedPaymentOnly,
      clientFeedbackMin: profileSuggestion.clientFeedbackMin ?? prev.clientFeedbackMin,
      dateRange: profileSuggestion.dateRange || prev.dateRange,
      hourlyRate: profileSuggestion.hourlyRate?.rangeStart != null
        ? profileSuggestion.hourlyRate
        : prev.hourlyRate,
    }));
    setErrors({});
  };

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleSave = async () => {
    const validationErrors = validateFilters(filters);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (!tenant?._id) return;

    setLoading(true);
    setSaveStatus('saving');
    try {
      await updateUpworkJobFilters(tenant._id, filters);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus(null), 3000);
      await refreshConfig();
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const statusIcon = saveStatus === 'saving'
    ? <Loader className="w-4 h-4 animate-spin" />
    : saveStatus === 'success'
      ? <CheckCircle className="w-4 h-4 text-green-500" />
      : saveStatus === 'error'
        ? <XCircle className="w-4 h-4 text-red-500" />
        : null;

  const showBudget = filters.jobType === 'ANY' || filters.jobType === 'FIXED';
  const showHourly = filters.jobType === 'ANY' || filters.jobType === 'HOURLY';

  // Merge profile skills with synced/taxonomy skills (profile first, deduped)
  const mergedSkills = (() => {
    const profileSkills = profileSuggestion?.skills || [];
    const syncedSkills = suggestions.skills || [];
    const seen = new Set();
    const result = [];
    for (const skill of [...profileSkills, ...syncedSkills]) {
      const key = skill.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        result.push(skill);
      }
    }
    return result;
  })();

  return (
    <AdminPageLayout>
      <AdminCard
        actions={statusIcon}
        bodyClassName="space-y-6"
      >
        {!canReadJobFilters() && !canUpdateJobFilters() && (
          <p className="text-sm text-red-600">You don't have permission to view job filters.</p>
        )}
        {!canEdit && (canReadJobFilters() || canUpdateJobFilters()) && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-xs text-yellow-800">
            You have read-only access. Contact an admin to update these filters.
          </div>
        )}

        {suggestions.source === 'default' && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div>
              <span className="font-medium">Showing common Upwork options.</span>
              {' '}
              More personalized suggestions will appear after your first job sync. Configure filters and sync jobs to get started.
            </div>
          </div>
        )}

        {suggestions.source === 'mixed' && (
          <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800">
            <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
            <div>
              Some suggestions are from your synced jobs, others are common Upwork options.
            </div>
          </div>
        )}

        {canEdit && profileSuggestion && (
          <div className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
            <Sparkles className="h-4 w-4 text-purple-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-purple-800">
                <span className="font-medium">Profile-based suggestions available</span>
                {profileMeta && (
                  <span className="text-purple-600">
                    {' '}&mdash; based on {profileMeta.totalSkills} skills and {profileMeta.totalJobs} completed jobs
                    {profileMeta.profileSource === 'upwork_page_import' ? ' from your imported profile snapshot' : ''}
                  </span>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={applyProfileSuggestions}
              className="flex-shrink-0 px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
            >
              Apply from Profile
            </button>
          </div>
        )}

        {canEdit && profileLoading && !profileSuggestion && (
          <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <Loader className="h-3.5 w-3.5 text-gray-400 animate-spin" />
            <p className="text-xs text-gray-500">Loading profile-based suggestions...</p>
          </div>
        )}

        <fieldset disabled={!canEdit} className={!canEdit ? 'opacity-80' : ''}>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2 space-y-6">
              {/* Section 1: Search & Targeting */}
              <Section title="Search & Targeting">
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  <AutocompleteTagInput
                    label="Search Terms"
                    suggestions={profileSuggestion?.searchTerms || []}
                    value={filters.searchTerms}
                    onChange={(v) => updateFilter('searchTerms', v)}
                    placeholder="Type a keyword and press Enter"
                  />
                  <AutocompleteTagInput
                    label="Skills"
                    suggestions={mergedSkills}
                    value={filters.skills}
                    onChange={(v) => updateFilter('skills', v)}
                    placeholder="Search skills..."
                  />
                </div>
                <div className="mt-4">
                  <label className="admin-filter-label">Category</label>
                  <select
                    value={filters.category}
                    onChange={(e) => updateFilter('category', e.target.value)}
                    className="admin-filter-control"
                  >
                    <option value="">Any Category</option>
                    {suggestions.categories.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </Section>

              {/* Section 4: Client Requirements */}
              <Section title="Client Requirements">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filters.verifiedPaymentOnly}
                      onChange={(e) => updateFilter('verifiedPaymentOnly', e.target.checked)}
                      className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Verified payment only</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={filters.previousClients}
                      onChange={(e) => updateFilter('previousClients', e.target.checked)}
                      className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">Previous clients only</span>
                  </label>
                  <div>
                    <label className="admin-filter-label">Min Client Hires</label>
                    <input
                      type="number"
                      value={filters.clientHiresMin ?? ''}
                      onChange={(e) => updateFilter('clientHiresMin', e.target.value ? Number(e.target.value) : null)}
                      className="admin-filter-control"
                      placeholder="Any"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="admin-filter-label">Min Client Rating</label>
                    <select
                      value={filters.clientFeedbackMin ?? ''}
                      onChange={(e) => updateFilter('clientFeedbackMin', e.target.value ? Number(e.target.value) : null)}
                      className="admin-filter-control"
                    >
                      <option value="">Any</option>
                      <option value="4">4.0+</option>
                      <option value="4.5">4.5+</option>
                      <option value="4.8">4.8+</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <AutocompleteTagInput
                    label="Client Location"
                    suggestions={suggestions.locations}
                    value={filters.locations}
                    onChange={(v) => updateFilter('locations', v)}
                    placeholder="Search countries..."
                  />
                </div>
              </Section>

              {/* Section 5: Recency & Proposals */}
              <Section title="Recency & Proposals">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="admin-filter-label">Posted Within</label>
                    <select
                      value={filters.dateRange}
                      onChange={(e) => updateFilter('dateRange', e.target.value)}
                      className="admin-filter-control"
                    >
                      <option value="ANY">Any Time</option>
                      <option value="24h">Last 24 Hours</option>
                      <option value="3d">Last 3 Days</option>
                      <option value="7d">Last 7 Days</option>
                      <option value="14d">Last 14 Days</option>
                      <option value="30d">Last 30 Days</option>
                    </select>
                  </div>
                  <RangeInput
                    label="Proposal Count Range"
                    value={filters.proposalRange}
                    onChange={(v) => updateFilter('proposalRange', v)}
                    error={errors.proposalRange}
                  />
                </div>
              </Section>
            </div>

            <div className="space-y-6">
              {/* Section 2: Job Type & Level */}
              <Section title="Job Type & Level">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="admin-filter-label">Job Type</label>
                    <select
                      value={filters.jobType}
                      onChange={(e) => updateFilter('jobType', e.target.value)}
                      className="admin-filter-control"
                    >
                      <option value="ANY">Any</option>
                      <option value="FIXED">Fixed Price</option>
                      <option value="HOURLY">Hourly</option>
                    </select>
                  </div>
                  <div>
                    <label className="admin-filter-label">Experience Level</label>
                    <select
                      value={filters.experienceLevel}
                      onChange={(e) => updateFilter('experienceLevel', e.target.value)}
                      className="admin-filter-control"
                    >
                      <option value="ANY">Any</option>
                      <option value="ENTRY_LEVEL">Entry Level</option>
                      <option value="INTERMEDIATE">Intermediate</option>
                      <option value="EXPERT">Expert</option>
                    </select>
                  </div>
                </div>
              </Section>

              {/* Section 3: Budget */}
              <Section title="Budget & Rates">
                <div className="space-y-4">
                  {showBudget && (
                    <RangeInput
                      label="Fixed Price Range (USD)"
                      value={filters.budgetRange}
                      onChange={(v) => updateFilter('budgetRange', v)}
                      error={errors.budgetRange}
                      prefix="$"
                    />
                  )}
                  {showHourly && (
                    <RangeInput
                      label="Hourly Rate Range (USD)"
                      value={filters.hourlyRate}
                      onChange={(v) => updateFilter('hourlyRate', v)}
                      error={errors.hourlyRate}
                      prefix="$"
                      suffix="/hr"
                    />
                  )}
                  {!showBudget && !showHourly && (
                    <p className="text-xs text-gray-400">Select a job type to configure budget filters.</p>
                  )}
                </div>
              </Section>
            </div>
          </div>
        </fieldset>

        {/* Footer */}
        <div className="flex justify-end pt-5 border-t border-gray-100">
          <button
            onClick={handleSave}
            disabled={loading || !canEdit}
            data-onboarding="save-job-filters-button"
            className="px-3 py-1 text-sm font-medium bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center gap-2 transition-colors"
          >
            <Save className="w-4 h-4" />
            Save Filters
          </button>
        </div>
      </AdminCard>
    </AdminPageLayout>
  );
}
